import { alpha, type Theme } from '@mui/material';
import { NEOBET_HEADER_HEIGHT, NEOBET_MOBILE_HEADER_HEIGHT } from '../../../../config/skinRegistry/configs';
import type { BetslipAnimationOrigin } from '../../../../utils/betslipAnimationOrigin';
import { NEOBET_COMPACT_DRAWER_WIDTH, NEOBET_DESKTOP_DRAWER_WIDTH } from '../constants';
import { getNeobetSidebarResizeTransition } from '../sidebarMotion';

export const HOME_PATH = '/';

export const styles = {
  appBar: {
    height: { xs: NEOBET_MOBILE_HEADER_HEIGHT, md: NEOBET_HEADER_HEIGHT },
    backgroundColor: 'background.paper',
  },
  toolbar: {
    width: '100%',
    minHeight: { xs: NEOBET_MOBILE_HEADER_HEIGHT, md: NEOBET_HEADER_HEIGHT },
    height: { xs: NEOBET_MOBILE_HEADER_HEIGHT, md: NEOBET_HEADER_HEIGHT },
    px: { xs: 1.5, md: 0 },
  },
  publicToolbar: {
    width: '100%',
    maxWidth: (theme: Theme) => theme.breakpoints.values.lg,
    mx: 'auto',
    px: { md: 2 },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1.5,
  },
  publicAuthActions: {
    alignItems: 'center',
    gap: 1,
    ml: 'auto',
  },
  homeLink: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    lineHeight: 0,
    color: 'inherit',
    textDecoration: 'none',
  },
  publicSignInButton: (theme: Theme) => ({
    minWidth: { xs: 64, md: 96 },
    height: { xs: 32, md: 40 },
    px: { xs: 1.25, md: 1.75 },
    borderRadius: 2.5,
    border: '1px solid',
    borderColor: alpha(theme.palette.common.white, 0.1),
    color: 'text.primary',
    backgroundColor: alpha(theme.palette.common.white, 0.03),
    backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.04)} 0%, transparent 100%)`,
    fontSize: { xs: '11px', md: '12px' },
    fontWeight: 800,
    letterSpacing: '0.02em',
    '&:hover': {
      borderColor: 'primary.main',
      color: 'common.white',
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
  }),
  authDesktopInner: {
    width: '100%',
    height: 1,
    display: 'flex',
    alignItems: 'center',
  },
  desktopSidebarSlot: (isCompact: boolean) => ({
    width: isCompact ? NEOBET_COMPACT_DRAWER_WIDTH : NEOBET_DESKTOP_DRAWER_WIDTH,
    minWidth: isCompact ? NEOBET_COMPACT_DRAWER_WIDTH : NEOBET_DESKTOP_DRAWER_WIDTH,
    height: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    px: 1.5,
    transition: getNeobetSidebarResizeTransition(isCompact),
    willChange: 'width',
  }),
  sidebarCollapseButton: {
    width: 40,
    height: 40,
    borderRadius: 2,
    backgroundColor: 'neutral.600',
    color: 'text.primary',
    '&:hover': {
      backgroundColor: 'neutral.500',
    },
  },
  desktopContentContainer: {
    width: 1,
    maxWidth: (theme: Theme) => theme.breakpoints.values.lg,
    mx: 'auto',
    px: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  spacer: {
    flex: 1,
  },
  utilityCluster: {
    alignItems: 'center',
    ml: 'auto',
    gap: 1,
  },
  balanceGroup: (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    p: 0.5,
    borderRadius: 999,
    backgroundColor: 'neutral.600',
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.12)}`,
  }),
  balanceValue: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 92,
    px: 1.5,
    py: 0.875,
    borderRadius: 999,
    backgroundColor: 'background.default',
    color: 'text.primary',
    lineHeight: 1,
  },
  utilityIconButton:
    (isActive = false) =>
    (theme: Theme) => ({
      width: 40,
      height: 40,
      borderRadius: 2.5,
      border: '1px solid',
      borderColor: isActive ? alpha(theme.palette.primary.main, 0.28) : 'divider',
      backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.12) : 'neutral.600',
      color: isActive ? 'primary.main' : 'text.primary',
      '&:hover': {
        backgroundColor: 'neutral.500',
      },
    }),
  mobileTopBar: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
  },
  mobileActions: {
    alignItems: 'center',
    gap: 0.75,
    ml: 'auto',
  },
  mobileBottomNav: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: (theme: Theme) => theme.zIndex.appBar,
    display: { xs: 'flex', md: 'none' },
    alignItems: 'stretch',
    justifyContent: 'space-around',
    px: 1,
    py: 0.75,
    pb: 'calc(env(safe-area-inset-bottom, 0px) + 6px)',
    backgroundColor: 'background.paper',
    borderTop: '1px solid',
    borderColor: 'divider',
    boxShadow: (theme: Theme) => `0 -12px 28px ${alpha(theme.palette.common.black, 0.24)}`,
  },
  mobileInfoMessageBubble: {
    position: 'absolute',
    left: '50%',
    bottom: 'calc(100% + 8px)',
    transform: 'translateX(-50%)',
    width: 'max-content',
    maxWidth: 'min(280px, calc(100vw - 16px))',
    display: 'grid',
    gap: 0.375,
    px: 1.25,
    py: 0.875,
    backgroundColor: 'neutral.600',
    border: '1px solid',
    borderColor: 'primary.main',
    borderRadius: 1.5,
    boxShadow: (theme: Theme) => `0 12px 24px ${alpha(theme.palette.common.black, 0.2)}`,
    pointerEvents: 'none',
    zIndex: (theme: Theme) => theme.zIndex.appBar + 1,
    '&::after': {
      content: '""',
      position: 'absolute',
      left: '50%',
      bottom: -7,
      width: 12,
      height: 12,
      backgroundColor: 'neutral.600',
      borderRight: '1px solid',
      borderBottom: '1px solid',
      borderColor: 'primary.main',
      transform: 'translateX(-50%) rotate(45deg)',
    },
  },
  mobileInfoMessage: {
    color: 'primary.main',
    typography: 'body4',
    fontWeight: 700,
    textAlign: 'center',
    lineHeight: 1.3,
  },
  mobileBetslipFab:
    (isVisible: boolean, animateEntrance = false) =>
    (theme: Theme) => ({
      position: 'fixed',
      left: '50%',
      bottom: 'calc(var(--mobile-bottom-nav-height) + env(safe-area-inset-bottom, 0px) + 20px)',
      transform: `translateX(-50%) translateY(${isVisible ? 0 : 12}px) scale(${isVisible ? 1 : 0.78})`,
      opacity: isVisible ? 1 : 0,
      width: 56,
      height: 56,
      borderRadius: 999,
      backgroundColor: 'primary.main',
      backgroundImage: `linear-gradient(180deg, ${theme.palette.primary[400]} 0%, ${theme.palette.primary[600]} 100%)`,
      color: 'neutral.800',
      zIndex: theme.zIndex.appBar + 1,
      boxShadow: `0 12px 24px ${alpha(theme.palette.common.black, 0.28)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.22)}`,
      transition: 'opacity 180ms ease, transform 220ms cubic-bezier(0.2, 0.9, 0.2, 1.15)',
      animation: animateEntrance ? 'neobet-betslip-fab-enter 220ms cubic-bezier(0.2, 0.9, 0.2, 1.15)' : 'none',
      '&:hover': {
        backgroundColor: 'primary.main',
      },
      '& svg': {
        width: 30,
        height: 30,
        display: 'block',
      },
      '@keyframes neobet-betslip-fab-enter': {
        '0%': {
          opacity: 0,
          transform: 'translateX(-50%) translateY(12px) scale(0.78)',
        },
        '100%': {
          opacity: 1,
          transform: 'translateX(-50%) translateY(0) scale(1)',
        },
      },
    }),
  mobileBetslipFabBadge: (animationMode: 'add' | 'remove' | null) => ({
    position: 'absolute',
    top: -5,
    right: -5,
    minWidth: 22,
    height: 22,
    px: 0.75,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'error.400',
    color: 'neutral.50',
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 10px 0px',
    typography: 'body2',
    fontWeight: 800,
    lineHeight: 1,
    zIndex: 1,
    animation:
      animationMode === 'add'
        ? 'neobet-betslip-badge-pop 320ms cubic-bezier(0.2, 0.9, 0.2, 1.25)'
        : animationMode === 'remove'
          ? 'neobet-betslip-badge-remove 240ms cubic-bezier(0.2, 0.9, 0.2, 1)'
          : 'none',
    '@keyframes neobet-betslip-badge-pop': {
      '0%': {
        transform: 'scale(0.8)',
        opacity: 1,
      },
      '55%': {
        transform: 'scale(1.2)',
        opacity: 0.75,
      },
      '100%': {
        transform: 'scale(1)',
        opacity: 1,
      },
    },
    '@keyframes neobet-betslip-badge-remove': {
      '0%': {
        transform: 'scale(1)',
        filter: 'brightness(1)',
      },
      '45%': {
        transform: 'scale(0.8)',
        filter: 'brightness(0.75)',
      },
      '100%': {
        transform: 'scale(1)',
        filter: 'brightness(1)',
      },
    },
  }),
  mobileBetslipFabFlyDot: (origin: BetslipAnimationOrigin, target: BetslipAnimationOrigin) => (theme: Theme) => ({
    position: 'fixed',
    left: 0,
    top: 0,
    width: 24,
    height: 24,
    borderRadius: 999,
    backgroundColor: 'error.main',
    boxShadow: `0 8px 14px ${alpha(theme.palette.common.black, 0.24)}`,
    opacity: 0,
    pointerEvents: 'none',
    zIndex: theme.zIndex.appBar + 2,
    '--neobet-betslip-origin-x': `${origin.x}px`,
    '--neobet-betslip-origin-y': `${origin.y}px`,
    '--neobet-betslip-target-x': `${target.x}px`,
    '--neobet-betslip-target-y': `${target.y}px`,
    animation: 'neobet-betslip-fly-dot 420ms cubic-bezier(0.2, 0.86, 0.22, 1)',
    '@keyframes neobet-betslip-fly-dot': {
      '0%': {
        opacity: 0.38,
        transform:
          'translate(calc(var(--neobet-betslip-origin-x) - 12px), calc(var(--neobet-betslip-origin-y) - 12px)) scale(0.55)',
      },
      '72%': {
        opacity: 0.54,
        transform:
          'translate(calc(var(--neobet-betslip-target-x) - 12px), calc(var(--neobet-betslip-target-y) - 12px)) scale(1)',
      },
      '100%': {
        opacity: 0,
        transform:
          'translate(calc(var(--neobet-betslip-target-x) - 12px), calc(var(--neobet-betslip-target-y) - 12px)) scale(0.35)',
      },
    },
  }),
  bottomNavItem: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    borderRadius: 2,
    py: 0.5,
    px: 0.5,
    color: 'neutral.50',
    '& svg': {
      width: 24,
      height: 24,
      display: 'block',
    },
  },
  bottomNavIconWrap: (isActive: boolean) => ({
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: isActive ? 'primary.main' : 'neutral.300',
  }),
  bottomNavLabel: {
    typography: 'caption',
    fontWeight: 800,
    lineHeight: 1.2,
    textTransform: 'uppercase',
  },
} as const;
